/**
 * Helpers that decide whether a given attachment can be rendered inline.
 * Shared between the row's preview affordance (gated on file name) and the
 * preview body (also has the BE-reported content-type to lean on).
 */

/**
 * Extensions we can render inline, mapped to the MIME type we stamp on the
 * preview blob (see {@link renderableMimeType}). Single source of truth for
 * both "is this previewable" and "what type does the blob need to render".
 */
const EXTENSION_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  bmp: 'image/bmp',
  tif: 'image/tiff',
  tiff: 'image/tiff',
};

export type PreviewKind = 'pdf' | 'image' | 'unsupported';

function extensionOf(fileName: string | null | undefined): string {
  const name = fileName ?? '';
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

/** Strip any parameters (`; charset=…`) and normalise a raw content-type. */
function bareType(contentType: string | null | undefined): string {
  return (contentType ?? '').toLowerCase().split(';', 1)[0]?.trim() ?? '';
}

export function previewKindFromFileName(fileName: string | null | undefined): PreviewKind {
  const mime = EXTENSION_MIME[extensionOf(fileName)];
  if (mime === undefined) return 'unsupported';
  return mime === 'application/pdf' ? 'pdf' : 'image';
}

/**
 * Resolve preview support from the BE content-type when available, falling
 * back to the file extension when the BE returns `application/octet-stream`.
 */
export function previewKindFromResponse(
  contentType: string | null | undefined,
  fileName: string | null | undefined,
): PreviewKind {
  const type = bareType(contentType);
  if (type.includes('pdf')) return 'pdf';
  if (type.startsWith('image/')) return 'image';
  return previewKindFromFileName(fileName);
}

export function isPreviewable(fileName: string | null | undefined): boolean {
  return previewKindFromFileName(fileName) !== 'unsupported';
}

/**
 * MIME type to stamp on the preview blob so the browser renders it inline
 * rather than downloading it. The documents API allow-lists
 * `application/octet-stream`, so PDFs/images are frequently served with that
 * generic type; an `<iframe>`/`<img>` pointed at an octet-stream blob URL
 * triggers a save-as (with the blob's UUID as the file name) instead of
 * rendering — this is the "random-named file" download in PTV2-1339. Prefer a
 * specific renderable server type; otherwise derive one from the extension.
 * Returns `null` when the file isn't previewable (blob left untouched).
 */
export function renderableMimeType(
  contentType: string | null | undefined,
  fileName: string | null | undefined,
): string | null {
  const type = bareType(contentType);
  if (type.includes('pdf')) return 'application/pdf';
  if (type.startsWith('image/')) return type;
  return EXTENSION_MIME[extensionOf(fileName)] ?? null;
}
